import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { Save, Building2, ShieldCheck, Loader2, Image as ImageIcon, Settings as SettingsIcon, Mail } from 'lucide-react';
import { toast } from 'react-toastify';
import { getBranding, updateBranding, BrandingData, API_HOST } from '../api/brandingApi';
import { EmailIntegration } from '../components/EmailIntegration';

const BrandingSettings = () => {
  const [formData, setFormData] = useState<BrandingData>({
    companyName: '',
    address: '',
    contactNumber: '',
    logoUrl: '',
    termsOfService: '',
    about: '',
    emailAddress: '',
    taxAmount: 0,
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const data = await getBranding();
        if (data) setFormData(data);
      } catch (err) {
        console.error('Failed to fetch branding data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBranding();
  }, []);

  useEffect(() => {
    // Cleanup preview URL to avoid memory leaks
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setLogoFile(file);

    if (file) {
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.companyName || !formData.contactNumber) {
      toast.error('Company Name and Contact Number are required.');
      return;
    }

    setSaving(true);

    try {
      await updateBranding({ ...formData, logoFile: logoFile || undefined });
      toast.success('Branding settings saved successfully!');
    } catch (err) {
      toast.error('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400">
        <Loader2 className="h-8 w-8 animate-spin mb-2" />
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="w-full animate-fade-in">
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm w-full">
        <div className="p-6 border-b border-slate-100 bg-slate-50/30">
          <h3 className="text-lg font-bold text-slate-900">Branding Details</h3>
          <p className="text-sm text-slate-500">Configure how your company appears across the platform.</p>
        </div>
        
        <form onSubmit={handleSave} className="p-6 space-y-5 text-left">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Company Name *</label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                placeholder="e.g. Strivre Services"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Contact Number *</label>
              <input
                type="text"
                value={formData.contactNumber}
                onChange={(e) => setFormData({ ...formData, contactNumber: e.target.value })}
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                placeholder="+1 (555) 000-0000"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Address</label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              rows={3}
              className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all resize-none"
              placeholder="Enter company address"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Terms of Service URL</label>
            <input
              type="text"
              value={formData.termsOfService}
              onChange={(e) => setFormData({ ...formData, termsOfService: e.target.value })}
              className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
              placeholder="Enter your company terms of service url..."
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">About Your Company</label>
            <textarea
              value={formData.about || ''}
              onChange={(e) => setFormData({ ...formData, about: e.target.value })}
              className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all resize-y min-h-[120px]"
              placeholder="Write a short description about your business..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Email Address</label>
              <input
                type="email"
                value={formData.emailAddress}
                onChange={(e) => setFormData({ ...formData, emailAddress: e.target.value })}
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                placeholder="Enter company email address"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Tax Rate (%)</label>
              <input
                type="number"
                step="0.01"
                value={formData.taxAmount}
                onChange={(e) => setFormData({ ...formData, taxAmount: parseFloat(e.target.value) || 0 })}
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                placeholder="Enter tax rate (%)"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Company Logo</label>
            <div className="flex flex-col gap-4">
              {/* Logo Preview / Placeholder */}
              <div className="min-w-[8rem] min-h-[8rem] w-fit max-w-[18rem] bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-center p-4 overflow-hidden shrink-0 shadow-sm transition-all duration-300">
                {logoPreview || formData.logoUrl ? (
                  <img 
                    src={
                      logoPreview || 
                      (formData.logoUrl 
                        ? (formData.logoUrl.startsWith('http') ? formData.logoUrl : `${API_HOST}${formData.logoUrl}`) 
                        : '')
                    } 
                    alt="Logo preview" 
                    className="max-h-24 w-auto object-contain border border-slate-100 rounded-lg p-1 bg-white shadow-sm"
                  />
                ) : (
                  <div className="w-20 h-20 bg-indigo-600 rounded-xl flex items-center justify-center font-bold text-white text-3xl uppercase shadow-md">
                    {formData.companyName?.charAt(0) || 'S'}
                  </div>
                )}
              </div>

              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="w-full max-w-sm bg-white border border-slate-200 rounded-xl px-4 py-2 text-slate-900 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                />
              </div>
            </div>
          </div>

          <div className="pt-2 border-t border-slate-100 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-8 py-3 rounded-2xl font-bold transition-all shadow-lg shadow-blue-200 active:scale-95"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save size={18} />}
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AdminSettings = () => (
  <div className="w-full bg-white border border-slate-200 rounded-2xl p-10 text-left animate-fade-in shadow-sm">
    <div className="flex flex-col">
      <ShieldCheck className="h-12 w-12 text-indigo-600 mb-6" />
      <h3 className="text-xl font-bold text-slate-900">Admin Access Required</h3>
      <p className="text-slate-500 mt-2">
        Advanced administrative controls will be available here in a future update.
      </p>
    </div>
  </div>
);

export default function Settings() {
  const location = useLocation();
  
  const isTabActive = (path: string) => {
    const normalizedPath = `/settings/${path}`;
    return location.pathname === normalizedPath || (path === 'branding' && location.pathname === '/settings');
  };

  const tabs = [
    { path: 'branding', label: 'Branding Settings', icon: <ImageIcon size={18} /> },
    { path: 'email', label: 'Email Integration', icon: <Mail size={18} /> },
    { path: 'admin', label: 'Admin Settings', icon: <Building2 size={18} /> },
  ] as const;

  return (
    <div className="min-h-screen pt-10 p-6 md:p-8 lg:p-10 md:ml-0">
      {/* Header */}
      <header className="mt-5 mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
            <SettingsIcon className="text-indigo-600" size={24} />
            Settings
          </h1>
          <p className="text-slate-500 text-sm mt-1 text-left">
            Manage your organization preferences and platform appearance.
          </p>
        </div>
      </header>

      {/* Tabs UI */}
      <div className="flex flex-col space-y-6">
        <div className="flex items-center gap-2 border-b border-slate-200 overflow-x-auto scrollbar-hide">
          {tabs.map((tab) => (
            <Link
              key={tab.path}
              to={`/settings/${tab.path}`}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-bold transition-all relative whitespace-nowrap rounded-t-xl ${
                isTabActive(tab.path)
                  ? 'text-blue-600 bg-blue-50/50'
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
              }`}
            >
              {tab.icon}
              {tab.label}
              {isTabActive(tab.path) && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-blue-600 rounded-t-full shadow-[0_-2px_8px_rgba(37,99,235,0.2)]" />
              )}
            </Link>
          ))}
        </div>

        {/* Tab Content */}
        <div className="pt-2">
          <Routes>
            <Route path="branding" element={<BrandingSettings />} />
            <Route path="email" element={<EmailIntegration />} />
            <Route path="admin" element={<AdminSettings />} />
            {/* Default redirect to branding */}
            <Route path="/" element={<Navigate to="branding" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}